import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";

// MUI
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Toolbar from "@mui/material/Toolbar";
import Drawer from "@mui/material/Drawer";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Button, IconButton, Stack } from "@mui/material";

// Bootstrap
import "bootstrap/dist/css/bootstrap.css"; // Import precompiled Bootstrap css
import "./sectionWiseClassification.styles.css";

// Components
import HeaderSection from "./components/header/header-section/HeaderSection";
import ProjectReport from "./components/project-list/project-report/ProjectReport";
import BidDetails from "./components/BidDetails";
import BidOverview from "./components/BidOverview";
import WinLoss from "./components/WinLoss";
import DashboardFilters from "./components/project-list/DashboardFilters";
import Divider from "./components/Divider";

// Hooks
import { useAppDispatch, useAppSelector } from "./redux/hooks/redux-hooks";

// Actions
import {
  fetchAllProjectsWithStatus,
  fetchProjectsWithStatusForUserId,
} from "./redux/actions/ProjectResponseAction";
import {
  fetchAllBDHeads,
  fetchAllBDPOCs,
  fetchAllSBGHeads,
  fetchAllSBUHeads,
} from "./redux/actions/GetUserNamesActions";
import { fetchParticularDashboardDetails } from "./redux/actions/DashboardDetailsActions";

// Utils
import { getProjectStatus, isValidArr } from "./utility/utils";

// Config
import { appConfig, projectBidStatusObj } from "./utility/config";

// Types
import {
  IFilterProjectModel,
  ProjectsSortOptionsModel,
} from "./models/ProjectResponseModel";
import { DashboardDetailsModel } from "./models/DashboardDetailsModel";
import { fetchSBUs } from "./redux/actions/SBUActions";
import { fetchUsers } from "./redux/actions/UserActions";
import BidCreationBtn from "./components/project-details/bid-creation-section/BidCreationBtn";
import { setUserId } from "./redux/actions/ProjectActions";

interface ISortOptionsListObj {
  [name: string]: ProjectsSortOptionsModel;
}

export type AddEdit = "add" | "edit";
export type Mode = "user" | "admin";

export enum ComponentName {
  Dashboard = "Dashboard",
  BidDetails = "BidDetails",
  ProjectReport = "ProjectReport",
  WinLoss = "WinLoss",
}

export default function SectionWiseClassification() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  // dashboard filters
  // To set the initial dates to fetch dashboard details
  const todayDate = new Date();
  let startingDate = new Date();
  const month = startingDate.getMonth() + 1;
  if (month === 1 || month === 2 || month === 3) {
    startingDate = new Date(`${startingDate.getFullYear() - 1}-04-01`);
  } else {
    startingDate = new Date(`${startingDate.getFullYear()}-04-01`);
  }
  const threeMonthsAfter = new Date(todayDate.setMonth(todayDate.getMonth()+3));

  const [fromDate, setFromDate] = useState(startingDate);
  const [endDate, setEndDate] = useState(threeMonthsAfter);
  const [dashboardDetails, setDashboardDetails] = useState<
    DashboardDetailsModel[]
  >([]);
  const [dashboardFilteredResponse, setDashboardFilteredResponse] = useState<
    DashboardDetailsModel[]
  >([]);
  //const [gridData, setGridData] = useState<any>({});
  const dashboaredResponse = useAppSelector(
    (state) => state.dashboardDetails.particular_DashboardDetail
  );

  const rightSectionRef = useRef<HTMLDivElement>(null); // To scroll page to the top on bid selection
  const [isFiltered, setIsFiltered] = useState(true);
  const [isSearched, setIsSearched] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bidType, setBidType] = useState<AddEdit>("add");
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const showDashboardVal = useMemo(() => {
    const projectId = searchParams.get("id");
    if (projectId) {
      return ComponentName.BidDetails;
    }
    return ComponentName.Dashboard;
  }, [searchParams]);

  const [currentComponent, setCurrentComponent] =
    useState<ComponentName>(showDashboardVal);
  const [allProjects, setAllProjects] = useState<IFilterProjectModel[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<
    IFilterProjectModel[]
  >([]);
  const [filters, setFilters] = useState({
    search: "",
    sbuHead: "all",
    bdHead: "all",
    sbgHead: "all",
    bdPoc: "all",
  });
  const [sortBy, setSortBy] = useState("all");

  const loggedInUser = useAppSelector((state) => state.user.loggedIn_User);
  const showProjectDetails = useAppSelector(
    (state) => state.showProject.showProject
  );
  const particularProjectSP = useAppSelector(
    (state) => state.projectSP.particular_ProjectSP
  );
  const all_projects = useAppSelector(
    (state) => state.projectResponse.project_Response
  );

  const isProjectsFetching = useAppSelector(
    (state) => state.projectResponse.isFetching
  );

  const projectsSortByFilters: ProjectsSortOptionsModel[] = useMemo(() => {
    const optionsList: ISortOptionsListObj = {};
    allProjects.forEach((rec) => {
      optionsList[rec.projectStatusKey] = {
        key: rec.projectStatusKey,
        value: rec.projectStatusLabel,
      };
    });

    return Object.values(optionsList);
  }, [allProjects]);

  // Redirect to admin pages for admin login
  useEffect(() => {
    if (loggedInUser.loggedIn && loggedInUser.roleId === 7) {
      const redirectPath = pathname.startsWith("/admin") ? pathname : "/admin";
      navigate(redirectPath, { replace: true });
    }
  }, [loggedInUser, navigate, pathname]);

  useEffect(() => {
    setAllProjects(() =>
      all_projects.map((rec) => {
        const res = getProjectStatus(rec, loggedInUser);
        return {
          ...rec,
          projectStatusLabel: res.value,
          projectStatusKey: res.key,
        };
      })
    );
  }, [all_projects, loggedInUser]);

  // To caluculate the counts of project statuses
  const projectStatusCounts = useMemo(() => {
    const counts = {
      bidUniverse: {
        count: 0,
        value: 0,
      },
      savedAsDraft: {
        count: 0,
        value: 0,
      },
      bidPipeline: {
        count: 0,
        value: 0,
      },
      submitted: {
        count: 0,
        value: 0,
      },
      gonogoApproved: {
        count: 0,
        value: 0,
      },
      gonogoRejected: {
        count: 0,
        value: 0,
      },
      bidSubmitted: {
        count: 0,
        value: 0,
      },
      win: {
        count: 0,
        value: 0,
      },
      loss: {
        count: 0,
        value: 0,
      },
    };

    function incProjStatusCount(
      key:
        | "savedAsDraft"
        | "bidPipeline"
        | "submitted"
        | "gonogoApproved"
        | "gonogoRejected"
        | "bidSubmitted"
        | "win"
        | "loss"
        | "bidUniverse",
      estValue: number
    ) {
      counts[key].count = counts[key].count + 1;
      counts[key].value = counts[key].value + estValue;
    }

    if (isValidArr(filteredProjects)) {
      filteredProjects.forEach((proj) => {
        incProjStatusCount("bidUniverse", proj.estimatedValue);
        if (proj.isBidDropped || proj.bidExploratoryStatus) {
          return;
        }
        if (proj.projectStatusKey === projectBidStatusObj.submitted) {
          incProjStatusCount("submitted", proj.estimatedValue);
          incProjStatusCount("bidPipeline", proj.estimatedValue);
        }
        if (proj.projectStatusKey === projectBidStatusObj.pendingForApproval) {
          incProjStatusCount("submitted", proj.estimatedValue);
          incProjStatusCount("bidPipeline", proj.estimatedValue);
        }
        if (proj.projectStatusKey === projectBidStatusObj.rejected) {
          incProjStatusCount("submitted", proj.estimatedValue);
          incProjStatusCount("gonogoRejected", proj.estimatedValue);
        }
        if (proj.projectStatusKey === projectBidStatusObj.approved) {
          incProjStatusCount("submitted", proj.estimatedValue);
          incProjStatusCount("bidPipeline", proj.estimatedValue);
          incProjStatusCount("gonogoApproved", proj.estimatedValue);
        }
        if (proj.projectStatusKey === "bidSubmitted") {
          incProjStatusCount("submitted", proj.estimatedValue);
          incProjStatusCount("gonogoApproved", proj.estimatedValue);
          incProjStatusCount("bidSubmitted", proj.estimatedValue);
        }
        if (proj.projectStatusKey === projectBidStatusObj.loss) {
          incProjStatusCount("submitted", proj.estimatedValue);
          incProjStatusCount("gonogoApproved", proj.estimatedValue);
          incProjStatusCount("bidSubmitted", proj.estimatedValue);
          incProjStatusCount("loss", proj.estimatedValue);
        }
        if (proj.projectStatusKey === projectBidStatusObj.win) {
          incProjStatusCount("submitted", proj.estimatedValue);
          incProjStatusCount("gonogoApproved", proj.estimatedValue);
          incProjStatusCount("bidSubmitted", proj.estimatedValue);
          incProjStatusCount("win", proj.estimatedValue);
        }
        if (proj.projectStatusKey === projectBidStatusObj.drafted) {
          incProjStatusCount("bidPipeline", proj.estimatedValue);
        }
        if (proj.projectStatusKey === projectBidStatusObj.savedAsDraft) {
          incProjStatusCount("bidPipeline", proj.estimatedValue);
          incProjStatusCount("savedAsDraft", proj.estimatedValue);
        }
      });
    }
    return counts;
  }, [filteredProjects]);

  // To identify first project selection in the list
  const isFilteredResponse = useMemo(() => {
    return isFiltered && isSearched;
  }, [isFiltered, isSearched]);

  useEffect(() => {
    if (loggedInUser.roleId === 5 || loggedInUser.roleId === 6) {
      dispatch(fetchAllProjectsWithStatus());
    } else {
      dispatch(
        fetchProjectsWithStatusForUserId(
          loggedInUser.userId,
          loggedInUser.roleId
        )
      );
    }

    dispatch(fetchAllBDPOCs(loggedInUser.userId));
    dispatch(fetchAllBDHeads(loggedInUser.userId));
    dispatch(fetchAllSBUHeads(loggedInUser.userId));
    dispatch(fetchAllSBGHeads(loggedInUser.userId));
    dispatch(fetchSBUs());
    dispatch(fetchUsers());
  }, [loggedInUser.roleId, loggedInUser.userId, dispatch]);

  //projects filters
  useEffect(() => {
    let filteredResponse = allProjects;
    if (sortBy !== "all") {
      const statusArr = sortBy.split(",");
      filteredResponse = filteredResponse.filter((rec) =>
        statusArr.includes(rec.projectStatusKey)
      );
    }

    if (filters.bdHead !== "all") {
      filteredResponse = filteredResponse.filter(
        (rec) => rec.bdHeadName === filters.bdHead
      );
    }

    if (filters.bdPoc !== "all") {
      filteredResponse = filteredResponse.filter(
        (rec) => rec.bdPoCName === filters.bdPoc
      );
    }

    if (filters.sbgHead !== "all") {
      filteredResponse = filteredResponse.filter(
        (rec) => rec.sbgName === filters.sbgHead
      );
    }

    if (filters.sbuHead !== "all") {
      filteredResponse = filteredResponse.filter(
        (rec) => rec.sbuName === filters.sbuHead
      );
    }

    if (filters.search) {
      const lowerCaseSearchVal = filters.search.toLowerCase();
      filteredResponse = filteredResponse.filter((rec) =>
        rec.projectName?.toLocaleLowerCase().includes(lowerCaseSearchVal)
      );
    }

    if (fromDate || endDate) {
      filteredResponse = filteredResponse.filter((rec) => {
        const bidSubmittedStatusDate = rec.bidSubmittedStatusDate && new Date(rec.bidSubmittedStatusDate);
        if (bidSubmittedStatusDate && fromDate <= bidSubmittedStatusDate && bidSubmittedStatusDate <= endDate) {
          return true;
        }
        return false;
      });
    }

    setFilteredProjects(filteredResponse);
    setIsFiltered(true);
  }, [filters, sortBy, allProjects, fromDate, endDate]);

  //filter for dashboards
  const handleSortByChange = useCallback((val: string) => {
    setSortBy(val);
    setIsSearched(true); // Should enable on any filters applies
    setIsFiltered(false); // should disable on any filters applied
  }, []);

  const handleFilters = useCallback((key: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setIsSearched(true); // Should enable on any filters applies
    setIsFiltered(false); // should disable on any filters applied
  }, []);

  const setBadgeClickValue = (val: string) => {
    handleSortByChange(val);
  };

  // To set searched and filtered flags as false to select first project as selected in bid list section
  const resetProjectSelectionFlags = useCallback(() => {
    setIsFiltered(false);
    setIsSearched(false);
  }, []);

  // To Scroll the page to the top on any bid selection
  const scrollToTopOnBidSelection = useCallback(() => {
    if (rightSectionRef.current) {
      rightSectionRef.current.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, []);

  // To show sidebar on click for mobile view
  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  //dashboard filters
  // To show and hide sidebar on click for mobile view
  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  // To close the drawer on selecting filters
  const handleDrawerOpen = () => {
    setMobileOpen(true);
  };

  const handleFromDate = (date: Date | null): void => {
    if (date) {
      setFromDate(date);
    }
    handleDrawerClose(); // To close the drawer on selecting filters
  };

  const handleEndDate = (date: Date | null): void => {
    if (date) {
      setEndDate(date);
    }
    handleDrawerClose(); // To close the drawer on selecting filters
  };
  // Logic to show the records in list view
  useEffect(() => {
    setDashboardDetails(dashboaredResponse);
  }, [dashboaredResponse]);

  useEffect(() => {
    setDashboardFilteredResponse(dashboardDetails);
  }, [dashboardDetails]);
  // Fetch dashboard details
  useEffect(() => {
    dispatch(
      fetchParticularDashboardDetails(
        loggedInUser.userId,
        true
        //currentComponent === ComponentName.Dashboard ? true : false
      )
    );
  }, [loggedInUser.userId, dispatch]);

  useEffect(() => {
    let result = dashboardDetails;

    if (filters.bdHead !== "all") {
      result = result.filter((rec) => rec.bdHead === filters.bdHead);
    }

    if (filters.bdPoc !== "all") {
      result = result.filter((rec) => rec.bdpoc === filters.bdPoc);
    }

    if (filters.sbgHead !== "all") {
      result = result.filter((rec) => rec.sbg === filters.sbgHead);
    }

    if (filters.sbuHead !== "all") {
      result = result.filter((rec) => rec.sbu === filters.sbuHead);
    }

    setDashboardFilteredResponse(result);
  }, [filters, dashboardDetails]);

  const handlePendingApprovalBidsClick = () => {
    setCurrentComponent(ComponentName.BidDetails);
    setBadgeClickValue("pending for approval");
  };

  //Hanlde Create Bid Panel Open
  const handleOpenBidPanel = (type: AddEdit) => {
    if (type === "add") {
      dispatch(setUserId(loggedInUser.userId));
    }
    setIsPanelOpen(true);
    setBidType(type);
  };

  const dismissPanel = () => {
    setIsPanelOpen(false);
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", width: "100vw" }}>
        <CssBaseline />
        <HeaderSection
          componentName={currentComponent}
          setCurrentComponent={setCurrentComponent}
          setBadgeClickValue={setBadgeClickValue}
          counts={projectStatusCounts}
          handleDrawerToggle={handleDrawerToggle}
        />
      </Box>
      <Box
        sx={{
          marginTop: 10,
          px: 1,
          position: "fixed",
          width: "100vw",
          zIndex: 2,
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: "280px" },
          }}
        >
          <div>
            <Toolbar sx={{ height: `${appConfig.headerHeight}px` }} />
            <DashboardFilters
              currentComponent={currentComponent}
              onChange={handleFilters}
              roleId={loggedInUser.roleId}
              filters={filters}
              handleFilters={handleFilters}
              fromDate={fromDate}
              endDate={endDate}
              handleFromDate={handleFromDate}
              handleEndDate={handleEndDate}
              statusFilters={projectsSortByFilters}
              statusValue={sortBy}
              statusChangeHandler={handleSortByChange}
              isFiltered={isFilteredResponse}
              resetFlags={resetProjectSelectionFlags}
              projects={filteredProjects}
              scrollToTopOnBidSelection={scrollToTopOnBidSelection}
            />
          </div>
        </Drawer>
        <IconButton
          color="inherit"
          aria-label="Dashboard filters"
          edge="start"
          onClick={handleDrawerOpen}
          sx={{ display: { md: "none" }, marginLeft: "5px" }}
        >
          <FilterAltIcon />
        </IconButton>
        <Box sx={{ display: { xs: "none", md: "flex" } }}>
          <DashboardFilters
            currentComponent={currentComponent}
            onChange={handleFilters}
            roleId={loggedInUser.roleId}
            filters={filters}
            handleFilters={handleFilters}
            fromDate={fromDate}
            endDate={endDate}
            handleFromDate={handleFromDate}
            handleEndDate={handleEndDate}
            statusFilters={projectsSortByFilters}
            statusValue={sortBy}
            statusChangeHandler={handleSortByChange}
            isFiltered={isFilteredResponse}
            resetFlags={resetProjectSelectionFlags}
            projects={filteredProjects}
            scrollToTopOnBidSelection={scrollToTopOnBidSelection}
            handleDrawerToggle={handleDrawerToggle}
          />
          <Divider disableMb={true} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Stack
            direction="row"
            spacing={{ xs: 1, md: 2 }}
            pt={1}
            pb={1}
            height="50px"
          >
            <Button
              variant={`${
                currentComponent === ComponentName.Dashboard
                  ? "contained"
                  : "text"
              }`}
              className="me-2"
              onClick={() => {
                setCurrentComponent(ComponentName.Dashboard);
                setEndDate(threeMonthsAfter);
              }}
            >
              Overview
            </Button>
            <Button
              variant={`${
                currentComponent === ComponentName.BidDetails
                  ? "contained"
                  : "text"
              }`}
              className="me-2"
              onClick={() => {
                setCurrentComponent(ComponentName.BidDetails);
                handleFilters("search", "");
                setEndDate(threeMonthsAfter);
              }}
            >
              Pipeline Pulse
            </Button>
            <Button
              variant={`${
                currentComponent === ComponentName.WinLoss
                  ? "contained"
                  : "text"
              }`}
              className="me-2"
              onClick={() => {
                setCurrentComponent(ComponentName.WinLoss);
                setEndDate(new Date());
              }
              }
            >
              Win Loss
            </Button>
            <Button
              variant={`${
                currentComponent === ComponentName.ProjectReport
                  ? "contained"
                  : "text"
              }`}
              onClick={() => {
                setCurrentComponent(ComponentName.ProjectReport);
                setEndDate(threeMonthsAfter);
              }}
            >
              Project Report
            </Button>
          </Stack>
          <Box sx={{ display: "flex", alignItems: "center", height: "50px" }}>
            {currentComponent === ComponentName.BidDetails &&
              loggedInUser &&
              loggedInUser.roleId &&
              (loggedInUser.roleId === 1 || loggedInUser.roleId === 2) && (
                <BidCreationBtn handleOpenBidPanel={handleOpenBidPanel} />
              )}

            {loggedInUser.roleId !== 1 && loggedInUser.roleId !== 2 && (
              <Button onClick={handlePendingApprovalBidsClick}>
                Pending Approval
              </Button>
            )}
          </Box>
        </Box>
        <Divider disableMb={true} />
      </Box>
      <Box component="main" marginTop="200px">
        {currentComponent === ComponentName.Dashboard && (
          <BidOverview
            dashboardFilteredResponse={dashboardFilteredResponse}
            filters={filters}
            fromDate={fromDate}
            endDate={endDate}
          />
        )}
        {currentComponent === ComponentName.BidDetails && (
          <BidDetails
            showProjectDetails={showProjectDetails}
            isFilteredResponse={isFilteredResponse}
            resetProjectSelectionFlags={resetProjectSelectionFlags}
            filteredProjects={filteredProjects}
            isProjectsFetching={isProjectsFetching}
            particularProjectSP={particularProjectSP}
            handleDrawerToggle={handleDrawerToggle}
            handleOpenBidPanel={handleOpenBidPanel}
            dismissPanel={dismissPanel}
            isPanelOpen={isPanelOpen}
            bidType={bidType}
          />
        )}
        {currentComponent === ComponentName.WinLoss && (
          <WinLoss
            dashboardFilteredResponse={dashboardFilteredResponse}
            fromDate={fromDate}
            endDate={endDate}
          />
        )}
        {currentComponent === ComponentName.ProjectReport && <ProjectReport />}
      </Box>
    </>
  );
}
